import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator/LoadingIndicator';
import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip/Tooltip';

import Button from '../../../components/Button';
import { formatPostedAtClockTime, SHORT_SHA_LENGTH } from '../releaseLogic';

import CiStatusBadge from './CiStatusBadge';
import CommitsBlock from './CommitsBlock';
import InlineMarkdown from './InlineMarkdown';
import PrerequisitesChecklist from './PrerequisitesChecklist';
import RiskCalloutsBanner from './RiskCalloutsBanner';
import TestPlanChecklist from './TestPlanChecklist';

import type {
  AdminGetReleaseChangeLogQuery,
  ReleasePreviewFieldsFragment,
} from '../__generated__/releaseOperations.gql';
import type {
  IAnnounceButtonState,
  IAnnounceJustPosted,
  IReleasePinCommit,
  IStartDraftButtonState,
} from '../releaseLogic';

/**
 * Spinner shared by the in-flight load and the brief post-response interstitial
 * (response arrived but Apollo hasn't written the cache yet). The "this can take
 * a while" copy is load-bearing: cold cache misses against the release-notes
 * model regularly run 30-60s and operators wonder if the page is broken.
 */
const renderGeneratingSpinner = () => (
  <div className="flex flex-col items-center gap-4 px-6 py-12">
    <LoadingIndicator type="line-spinner" size="lg" />
    <div className="flex max-w-md flex-col items-center gap-1 text-center">
      <h2 className="text-fg-primary text-base font-semibold">Generating release notes…</h2>
      <p className="text-fg-secondary text-sm">
        This can take 30 to 60 seconds while we summarize commits since the last release. Hang tight — leave the page
        open.
      </p>
    </div>
  </div>
);

interface IReleasePinLabelProps {
  /** Authoritative sha of the pin — the fallback when its commit row isn't in hand. */
  pinnedSha: string;
  pinnedCommit: IReleasePinCommit | null;
}

/**
 * Names the commit a draft would be cut at: its PR number as a link to the PR,
 * then the commit subject. A bare sha is technically precise and operationally
 * useless — an operator deciding whether the pin is far enough back needs to
 * recognize the change, which means its title and a way to open it.
 *
 * Falls back to the short sha for a commit that carries no PR (a direct push, or
 * a squash subject without the trailing `(#NNN)`), and to the sha alone when the
 * pin and the commit list came from different fetches.
 */
const ReleasePinLabel = ({ pinnedSha, pinnedCommit }: IReleasePinLabelProps) => {
  if (!pinnedCommit) {
    return <span className="text-fg-primary font-mono">{pinnedSha.slice(0, SHORT_SHA_LENGTH)}</span>;
  }
  return (
    <>
      {pinnedCommit.prUrl && pinnedCommit.prNumber != null ? (
        <a
          href={pinnedCommit.prUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="text-fg-brand-primary hover:text-fg-brand-primary_alt font-mono underline underline-offset-2"
        >
          #{pinnedCommit.prNumber}
        </a>
      ) : (
        <span className="text-fg-primary font-mono">{pinnedCommit.shortSha}</span>
      )}{' '}
      <span className="text-fg-primary">{pinnedCommit.subject}</span>
    </>
  );
};

interface IPendingReleaseCardProps {
  /** The generated preview, or `null` until the worker has written its cache row. */
  preview: ReleasePreviewFieldsFragment | null;
  /** False until the operator opts in — preview generation is never automatic. */
  previewCalled: boolean;
  previewLoading: boolean;
  previewError: { message?: string } | null;
  /** The poll gave up waiting for the worker; the operator can retry. */
  pollingTimedOut: boolean;
  /** GitHub-derived facts shown on the idle screen before any LLM call. */
  changeLog: AdminGetReleaseChangeLogQuery['adminGetReleaseChangeLog'] | null;
  changeLogLoading: boolean;
  startDraftButtonState: IStartDraftButtonState;
  /** Announce button mode/label/tooltip; `null` before a preview exists. */
  buttonState: IAnnounceButtonState | null;
  justPosted: IAnnounceJustPosted | null;
  announceLoading: boolean;
  /** Held true through GitHub's read-after-write window so announce can't double-fire. */
  isWaitingForDraftPropagation: boolean;
  draftLoading: boolean;
  /** Slack channel the announce will post to, threaded through so the label can't lie. */
  slackChannel: string;
  onStartDraft: () => void;
  onRetry: () => void;
  onAnnounce: () => void;
}

/**
 * The "nothing is drafted yet" half of the Releases tool, and the sibling of
 * `CurrentDraftCard` — the scene renders exactly one of the two.
 *
 * Walks four states: an idle call-to-action over the pending changelog, the
 * generating spinner, the timed-out and error recoveries, and finally the
 * rendered preview with its sticky announce header.
 */
const PendingReleaseCard = ({
  preview,
  previewCalled,
  previewLoading,
  previewError,
  pollingTimedOut,
  changeLog,
  changeLogLoading,
  startDraftButtonState,
  buttonState,
  justPosted,
  announceLoading,
  isWaitingForDraftPropagation,
  draftLoading,
  slackChannel,
  onStartDraft,
  onRetry,
  onAnnounce,
}: IPendingReleaseCardProps) => (
  <section aria-label="Pending release" className="border-border-primary border">
    {!previewCalled ? (
      /*
        Idle CTA — preview generation is deferred until the operator
        explicitly opts in. This is the cold-start state when no draft
        exists and the operator hasn't asked for one yet. Avoids
        burning an LLM cache miss every time someone opens /releases
        to glance at the history table or the open draft.

        The lightweight changelog (commits + base/head SHA + CI
        status) is fetched eagerly via `adminGetReleaseChangeLog`
        and rendered below the button so operators can preview
        pending changes before committing to release-notes
        generation.
      */
      <div className="flex flex-col gap-6 px-6 py-10">
        <div className="flex flex-col items-center gap-4">
          <div className="flex max-w-md flex-col items-center gap-1 text-center">
            <h2 className="text-fg-primary text-base font-semibold">No release in progress</h2>
            <p className="text-fg-secondary text-sm">
              Start a new draft to generate release notes from commits since the last release.
            </p>
          </div>
          {/*
            The pin, stated above the button rather than left implicit. A draft
            is cut at the newest green commit, which is not always HEAD, and an
            operator who can't see which commit they are about to release has no
            way to catch a pin that has fallen further back than they expect.
          */}
          {startDraftButtonState.pinnedSha && (
            <div className="flex max-w-2xl flex-col items-center gap-0.5 text-center">
              <span className="text-fg-secondary text-xs">
                Releases at{' '}
                <ReleasePinLabel
                  pinnedSha={startDraftButtonState.pinnedSha}
                  pinnedCommit={startDraftButtonState.pinnedCommit}
                />
                {startDraftButtonState.excludedCommitCount === 0 && ' — CI is green at head'}
              </span>
              {startDraftButtonState.excludedCommitCount > 0 && (
                <span className="text-fg-warning-primary text-xs">
                  Last commit with green CI — the {startDraftButtonState.excludedCommitCount} newer{' '}
                  {startDraftButtonState.excludedCommitCount === 1 ? 'commit is' : 'commits are'} excluded.
                </span>
              )}
            </div>
          )}
          {startDraftButtonState.isEnabled ? (
            <Button color="primary" size="md" onClick={() => void onStartDraft()}>
              Start a draft release
            </Button>
          ) : (
            <Tooltip title={startDraftButtonState.tooltip} placement="top">
              <TooltipTrigger>
                <span className="inline-block">
                  <Button color="primary" size="md" isDisabled>
                    Start a draft release
                  </Button>
                </span>
              </TooltipTrigger>
            </Tooltip>
          )}
          {/*
            A rejected start leaves the scene on this screen — nothing was
            generated, so there is no preview to swap to an error panel. The
            server rejects a pin whose CI went red between the last poll and the
            click, which is an ordinary race, so it has to say so here or the
            button reads as doing nothing.
          */}
          {!previewCalled && previewError && (
            <p className="text-fg-error-primary max-w-md text-center text-xs">
              {previewError.message || 'Could not start the draft. Retry, or check Sentry.'}
            </p>
          )}
        </div>

        {/*
          Pending-changes preview. Loading state is intentionally
          understated — the operator's primary action is the button
          above; the changelog is reference material.
        */}
        <div className="border-border-primary border-t pt-5">
          {changeLogLoading && !changeLog ? (
            <span className="text-fg-tertiary text-xs italic">Loading pending changes…</span>
          ) : !changeLog ? (
            <span className="text-fg-tertiary text-xs italic">Pending changes unavailable.</span>
          ) : changeLog.commits.length === 0 ? (
            <span className="text-fg-tertiary text-xs italic">
              No commits since {changeLog.baseTag || 'the last release'} — nothing to release.
            </span>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <div className="flex flex-col">
                  <span className="text-fg-tertiary text-[10px] uppercase tracking-widest">Next tag</span>
                  <span className="text-fg-primary font-mono text-base font-semibold">{changeLog.computedNextTag}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-fg-tertiary text-[10px] uppercase tracking-widest">Reviewed head</span>
                  <span className="text-fg-secondary font-mono text-xs">
                    {changeLog.headSha.slice(0, SHORT_SHA_LENGTH)}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-fg-tertiary text-[10px] uppercase tracking-widest">Base</span>
                  <span className="text-fg-secondary font-mono text-xs">
                    {changeLog.baseTag} ({changeLog.baseSha.slice(0, SHORT_SHA_LENGTH)})
                  </span>
                </div>
                <div className="self-center">
                  <CiStatusBadge
                    conclusion={changeLog.ciStatus.conclusion}
                    successCount={changeLog.ciStatus.successCount}
                    failureCount={changeLog.ciStatus.failureCount}
                    cancelledCount={changeLog.ciStatus.cancelledCount}
                    pendingCount={changeLog.ciStatus.pendingCount}
                    totalCount={changeLog.ciStatus.totalCount}
                  />
                </div>
              </div>
              <CommitsBlock commits={changeLog.commits} baseTag={changeLog.baseTag} />
            </div>
          )}
        </div>
      </div>
    ) : previewLoading ? (
      /*
        Generating state — covers the in-flight spinner from the moment
        the start mutation fires through to the COMPLETED poll. The
        loading branch runs **before** `previewError` so a Retry click
        swaps the scene back to the spinner immediately while the new
        start mutation is in flight.
      */
      renderGeneratingSpinner()
    ) : pollingTimedOut && !preview ? (
      /*
        Polling cap hit (default 90 s) without the worker writing a
        cache row. Either the worker is still in flight against an
        outlier-large diff, or it errored silently. Operator-visible
        recovery: click Retry to re-fire the start mutation
        idempotently — on cache hit (most likely if the worker
        eventually finished) we flip straight to the rendered
        preview; on miss we re-dispatch the worker.
      */
      <div className="flex flex-col items-center gap-4 px-6 py-12">
        <div className="flex max-w-md flex-col items-center gap-1 text-center">
          <h2 className="text-fg-primary text-base font-semibold">Still generating release notes</h2>
          <p className="text-fg-secondary text-sm">
            The worker is taking longer than 90 seconds. Click Retry to check again — if it has finished, the preview
            will load instantly. If it consistently fails, check Sentry for the underlying error.
          </p>
        </div>
        <Button color="primary" size="md" onClick={() => void onRetry()}>
          Retry
        </Button>
      </div>
    ) : previewError ? (
      /*
        Error state — covers a failed start mutation (couldn't reach
        the Fargate worker) and any pollError. Without this branch
        the operator would be stuck on the spinner forever. Click
        Retry to re-fire the start mutation.
      */
      <div className="flex flex-col items-center gap-4 px-6 py-12">
        <div className="flex max-w-md flex-col items-center gap-1 text-center">
          <h2 className="text-fg-error-primary text-base font-semibold">Failed to generate release notes</h2>
          <p className="text-fg-secondary text-sm">
            {previewError.message || 'The release-notes pipeline returned an error.'} Retry, or check Sentry for the
            underlying failure.
          </p>
        </div>
        <Button color="primary" size="md" onClick={() => void onRetry()}>
          Retry
        </Button>
      </div>
    ) : !preview ? (
      /*
        Post-response interstitial: the response arrived but
        Apollo hasn't written the cache yet, so `data` is still
        undefined. Same spinner as the loading state.
      */
      renderGeneratingSpinner()
    ) : (
      <>
        {/*
        Sticky action header — tag identity, base/head SHAs, CI status,
        and the Announce button. Stays pinned to viewport top while
        scrolling the body so the operator never loses sight of the
        action button.

        Solid `bg-app` matches the body — mops-web's `bg-bg-*` semantic
        tokens use alpha in dark mode (designed to overlay, not be
        opaque), so a translucent sticky would let scrolling content
        bleed through. The shadow + bottom border provide the visual
        "elevated" cue.
      */}
        <div className="bg-app border-border-primary sticky top-0 z-10 border-b px-4 py-3 shadow-lg shadow-black/40">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="flex flex-col">
              <span className="text-fg-tertiary text-[10px] uppercase tracking-widest">Next tag</span>
              <span className="text-fg-primary font-mono text-2xl font-semibold">{preview.computedNextTag}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-fg-tertiary text-[10px] uppercase tracking-widest">Reviewed head</span>
              <span className="text-fg-secondary font-mono text-sm">{preview.headSha.slice(0, SHORT_SHA_LENGTH)}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-fg-tertiary text-[10px] uppercase tracking-widest">Base</span>
              <span className="text-fg-secondary font-mono text-sm">
                {preview.baseTag} ({preview.baseSha.slice(0, SHORT_SHA_LENGTH)})
              </span>
            </div>
            <div className="self-center">
              <CiStatusBadge
                conclusion={preview.ciStatus.conclusion}
                successCount={preview.ciStatus.successCount}
                failureCount={preview.ciStatus.failureCount}
                cancelledCount={preview.ciStatus.cancelledCount}
                pendingCount={preview.ciStatus.pendingCount}
                totalCount={preview.ciStatus.totalCount}
              />
            </div>
            <div className="ml-auto flex flex-wrap items-center justify-end gap-3">
              {justPosted && (
                <span className="text-fg-secondary text-xs">
                  Posted at {formatPostedAtClockTime(justPosted.postedAt)}
                  {justPosted.slackUrl && (
                    <>
                      {' — '}
                      <a
                        href={justPosted.slackUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="text-fg-brand-primary hover:text-fg-brand-primary_alt underline underline-offset-2"
                      >
                        view in Slack
                      </a>
                    </>
                  )}
                </span>
              )}
              {buttonState && buttonState.mode === 'disabled' ? (
                <Tooltip title={buttonState.tooltip ?? ''} placement="top">
                  <TooltipTrigger>
                    <span className="inline-block">
                      <Button color="secondary" size="sm" isDisabled>
                        {buttonState.label}
                      </Button>
                    </span>
                  </TooltipTrigger>
                </Tooltip>
              ) : (
                <Button
                  color="primary"
                  size="sm"
                  isLoading={announceLoading || isWaitingForDraftPropagation}
                  isDisabled={!buttonState || draftLoading}
                  onClick={() => void onAnnounce()}
                >
                  {isWaitingForDraftPropagation
                    ? 'Waiting for draft to propagate…'
                    : (buttonState?.label ?? `Announce pre-release in ${slackChannel}`)}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/*
        Body. Risks first (banner), then summary (full width), then a
        two-column workflow grid (prereqs | test plan), then commits
        (collapsed reference data).
      */}
        <div className="flex flex-col gap-5 px-4 py-4">
          {/*
            Set the expectation before the operator commits: announcing is
            what dispatches the benchmark, and Publish then waits on it.
            Stated here rather than in a widget because there is no run to
            render until the draft exists.
          */}
          <p className="text-fg-quaternary text-[11px] italic">
            Announcing dispatches the assistant benchmark for this commit — Publish waits for it to finish.
          </p>

          <RiskCalloutsBanner riskCallouts={preview.riskCallouts} />

          <div className="flex flex-col gap-2">
            <h2 className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">Summary</h2>
            {preview.summary.length === 0 ? (
              <p className="text-fg-tertiary text-xs italic">No summary generated.</p>
            ) : (
              <ul className="text-fg-primary marker:text-fg-tertiary flex list-disc flex-col gap-1 pl-5 text-sm">
                {preview.summary.map((line, index) => (
                  <li key={index}>
                    <InlineMarkdown text={line} />
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* MOPS targets a 1280px minimum viewport (see mops-web/AGENTS.md), so the prereqs / test-plan two-column grid is unconditional — no responsive breakpoint. */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-5">
            <div className="flex flex-col gap-2">
              <h2 className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">Prerequisites</h2>
              {/*
              Prereqs are extracted by the LLM from squash-merge commit
              bodies. The model sometimes invents steps or misses one
              buried in a long body — set that expectation **above**
              the list so an unfamiliar release manager reads the
              caveat before scanning the items, not after.
            */}
              {preview.prerequisites.length > 0 && (
                <p className="text-fg-quaternary text-[11px] italic">
                  ⚠ Generated from PR bodies — verify against the source PR before relying on these.
                </p>
              )}
              <PrerequisitesChecklist
                items={preview.prerequisites}
                commits={preview.commits}
                emptyCopy="No prerequisites required for this release."
              />
            </div>
            <div className="flex flex-col gap-2">
              <h2 className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">
                Suggested pre-release test plan
              </h2>
              <TestPlanChecklist
                items={preview.testPlan}
                emptyCopy="No frontend-verifiable test plan items for this release."
              />
            </div>
          </div>

          <CommitsBlock commits={preview.commits} baseTag={preview.baseTag} />
        </div>
      </>
    )}
  </section>
);

export default PendingReleaseCard;
